import { useState, useRef } from "react";
import authApi from "../api/auth";
import { motion, AnimatePresence } from "framer-motion";
import { GoogleLogin } from "@react-oauth/google";
import {
  Shield, BookOpen, Target, Zap, Trophy, Lock,
  Building2, MapPin, Globe, AlertTriangle, Mail, KeyRound,
  ArrowLeft, ChevronRight, User, Phone,
} from "lucide-react";

const EXAM_TAGS = ["NDA", "CDS", "AFCAT", "CAPF", "TA"];

const EXAM_META = {
  NDA:   { emoji: "⚔️",  color: "#C9A227", desc: "National Defence Academy" },
  CDS:   { emoji: "🎖️",  color: "#16A34A", desc: "Combined Defence Services" },
  AFCAT: { emoji: "✈️",  color: "#2563EB", desc: "Air Force Common Admission Test" },
  CAPF:  { emoji: "🛡️",  color: "#7C3AED", desc: "Central Armed Police Forces" },
  TA:    { emoji: "🪖",  color: "#92400E", desc: "Territorial Army" },
};

const DEFENCE_FEATURES = [
  { icon: <Target size={18} />,   title: "Daily Current Affairs",   desc: "Exam-mapped CA with MCQs from top IAS sources",             color: "#C9A227" },
  { icon: <BookOpen size={18} />, title: "GK Library",              desc: "Lucent-based topics: Polity, History, Geography, Science", color: "#3B82F6" },
  { icon: <Zap size={18} />,      title: "Practice MCQs",           desc: "Exam-pattern questions with detailed explanations",          color: "#10B981" },
  { icon: <Trophy size={18} />,   title: "Personalised Dashboard",  desc: "Content filtered for your exam — NDA, CDS, AFCAT & more",   color: "#8B5CF6" },
];

export default function LoginPage({ onSendOTP, onVerifyOTP, onSSOLogin, subdomain }) {
  // step: "email" → "profile" (new user) or straight to "otp" (existing user)
  const [step, setStep]             = useState("email");
  const [email, setEmail]           = useState("");
  const [firstName, setFirstName]   = useState("");
  const [lastName, setLastName]     = useState("");
  const [phone, setPhone]           = useState("");
  const [targetExam, setTargetExam] = useState("");
  const [formErr, setFormErr]       = useState("");
  const [otp, setOtp]               = useState(["", "", "", "", "", ""]);
  const [otpErr, setOtpErr]         = useState("");
  const [loading, setLoading]       = useState(false);
  const [countdown, setCountdown]   = useState(0);
  const otpRefs = useRef([]);

  const { school, loading: schoolLoading, error: schoolError, isSubdomain } = subdomain || {};

  const startCountdown = () => {
    setCountdown(60);
    const timer = setInterval(() => {
      setCountdown(prev => { if (prev <= 1) { clearInterval(timer); return 0; } return prev - 1; });
    }, 1000);
  };

  /* ── Step 1: check email → route to profile form or OTP ── */
  const handleContinue = async () => {
    setFormErr("");
    if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) return setFormErr("Please enter a valid email address.");
    setLoading(true);
    try {
      const { exists } = await authApi.checkEmail(email.trim());
      if (exists) {
        // Existing user — send OTP immediately, skip profile form
        await onSendOTP(email.trim(), {});
        setStep("otp");
        startCountdown();
      } else {
        // New user — show profile form
        setStep("profile");
      }
    } catch (e) {
      setFormErr(e.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  /* ── Step 2 (new users): send OTP with profile meta ── */
  const handleSendOTP = async () => {
    setFormErr("");
    if (!firstName.trim()) return setFormErr("Please enter your first name.");
    if (!targetExam)        return setFormErr("Please select the exam you are preparing for.");
    setLoading(true);
    try {
      await onSendOTP(email.trim(), {
        firstName: firstName.trim(),
        lastName:  lastName.trim(),
        phone:     phone.trim(),
        targetExam,
      });
      setStep("otp");
      startCountdown();
    } catch (e) {
      setFormErr(e.message || "Failed to send OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  /* ── Resend OTP ── */
  const handleResendOTP = async () => {
    setFormErr("");
    setLoading(true);
    try {
      await onSendOTP(email.trim(), { firstName, lastName, phone, targetExam });
      startCountdown();
    } catch (e) {
      setFormErr(e.message || "Failed to resend OTP.");
    } finally {
      setLoading(false);
    }
  };

  /* ── OTP input ── */
  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const next = [...otp];
    next[index] = value.slice(-1);
    setOtp(next);
    if (value && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) otpRefs.current[index - 1]?.focus();
    else if (e.key === "Enter") handleVerifyOTP();
  };

  /* ── Verify OTP ── */
  const handleVerifyOTP = async () => {
    setOtpErr("");
    const otpStr = otp.join("");
    if (otpStr.length !== 6) { setOtpErr("Please enter the 6-digit OTP."); return; }
    setLoading(true);
    try {
      await onVerifyOTP(email.trim(), otpStr);
    } catch (e) {
      setOtpErr(e.message || "Invalid OTP. Please try again.");
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (cred) => {
    setFormErr("");
    setLoading(true);
    try { await onSSOLogin(cred.credential); }
    catch (e) { setFormErr(e.message || "Google sign-in failed."); }
    finally { setLoading(false); }
  };

  /* ── Subdomain states ── */
  if (isSubdomain && schoolLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-[#050A15]">
        <div className="text-center">
          <span className="w-8 h-8 border-2 border-white/10 border-t-[#C9A227] rounded-full animate-spin inline-block mb-4" />
          <p className="text-white/40 font-sora text-sm">Loading school portal…</p>
        </div>
      </div>
    );
  }

  if (isSubdomain && schoolError === "school_not_found") {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-[#050A15]">
        <div className="text-center max-w-md px-6">
          <div className="w-16 h-16 rounded-2xl bg-amber-500/10 flex items-center justify-center mx-auto mb-5">
            <AlertTriangle size={28} className="text-amber-400" />
          </div>
          <h2 className="font-sora font-extrabold text-2xl text-white mb-2">School Not Found</h2>
          <p className="text-white/40 text-sm leading-relaxed mb-6">
            The portal <strong className="text-white/60">{subdomain.slug}.edupulseai.co</strong> does not exist or is inactive.
          </p>
          <a href={`${window.location.protocol}//app.edupulseai.co.in`}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#C9A227] text-[#050A15] text-sm font-bold font-sora no-underline">
            Go to Main Portal
          </a>
        </div>
      </div>
    );
  }

  const showSchool = isSubdomain && school;

  return (
    <div className="fixed inset-0 flex font-body"
      style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}>

      {/* ── LEFT PANEL ── */}
      <div className="hidden lg:flex w-[55%] relative overflow-hidden flex-col justify-between p-12 xl:p-16"
        style={{ background: "linear-gradient(160deg, #050A15 0%, #0A1628 50%, #0D1E36 100%)" }}>

        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(201,162,39,0.06) 0%, transparent 60%)" }} />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(30,64,175,0.08) 0%, transparent 60%)" }} />
        <div className="absolute inset-0 bg-[repeating-linear-gradient(135deg,rgba(255,255,255,.008)_0px,rgba(255,255,255,.008)_1px,transparent_1px,transparent_48px)] pointer-events-none" />

        <motion.div className="relative z-10" initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="flex items-center gap-3 mb-12">
            <div className="w-11 h-11 rounded-xl overflow-hidden shrink-0" style={{ boxShadow: "0 4px 20px rgba(201,162,39,0.3)" }}>
              <img src="/logo192.png" alt="EduPulse" className="w-full h-full object-cover" />
            </div>
            <div className="font-sora font-extrabold text-[22px] text-white leading-none">
              EduPulse<span style={{ color: "#C9A227" }}>AI</span>
            </div>
          </div>

          {showSchool ? (
            <>
              <h1 className="font-sora font-extrabold text-[40px] xl:text-[48px] text-white leading-[1.08] max-w-[480px]">{school.name}</h1>
              <div className="flex flex-col gap-2 mt-5">
                {school.city && <div className="flex items-center gap-2 text-white/30 text-[13px]"><MapPin size={13} /><span>{[school.city, school.state].filter(Boolean).join(", ")}</span></div>}
                {school.board && <div className="flex items-center gap-2 text-white/30 text-[13px]"><Building2 size={13} /><span>{school.board} Board</span></div>}
                {school.website && <div className="flex items-center gap-2 text-white/30 text-[13px]"><Globe size={13} /><span>{school.website.replace(/^https?:\/\//, "")}</span></div>}
              </div>
            </>
          ) : (
            <>
              <div className="flex flex-wrap gap-2 mb-8">
                {EXAM_TAGS.map(tag => (
                  <span key={tag} className="px-3 py-1 rounded-full text-[11px] font-bold font-sora tracking-wide"
                    style={{ background: "rgba(201,162,39,0.12)", color: "#C9A227", border: "1px solid rgba(201,162,39,0.2)" }}>
                    {tag}
                  </span>
                ))}
              </div>
              <h1 className="font-sora font-extrabold text-[42px] xl:text-[50px] text-white leading-[1.06] max-w-[480px]">
                Your Defence<br />
                <span style={{ background: "linear-gradient(90deg, #C9A227, #F59E0B)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                  Exam Partner.
                </span>
              </h1>
              <p className="text-[15px] mt-5 max-w-[400px] leading-[1.9]" style={{ color: "rgba(255,255,255,0.3)" }}>
                Daily current affairs, Lucent GK, exam-pattern MCQs — personalised for your exam.
              </p>
            </>
          )}
        </motion.div>

        <div className="relative z-10">
          <div className="grid grid-cols-2 gap-3">
            {DEFENCE_FEATURES.map((f, i) => (
              <motion.div key={i}
                className="flex items-start gap-3 p-4 rounded-2xl"
                style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.05)" }}
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + i * 0.1, duration: 0.4 }}>
                <div className="w-9 h-9 rounded-xl shrink-0 flex items-center justify-center mt-0.5"
                  style={{ background: `${f.color}15`, border: `1px solid ${f.color}20`, color: f.color }}>
                  {f.icon}
                </div>
                <div>
                  <div className="text-[13px] text-white/80 font-semibold font-sora">{f.title}</div>
                  <div className="text-[11px] mt-0.5 leading-relaxed" style={{ color: "rgba(255,255,255,0.28)" }}>{f.desc}</div>
                </div>
              </motion.div>
            ))}
          </div>
          <div className="flex items-center gap-2 mt-7" style={{ color: "rgba(255,255,255,0.15)", fontSize: 11 }}>
            <Lock size={12} />
            <span>256-bit SSL encryption · Data protected · Trusted by 10,000+ aspirants</span>
          </div>
        </div>
      </div>

      {/* ── RIGHT PANEL ── */}
      <div className="w-full lg:w-[45%] flex items-start lg:items-center justify-center p-5 sm:p-8 xl:p-12 overflow-y-auto"
        style={{ background: "#F8FAFC" }}>

        <motion.div className="w-full max-w-md pt-4 lg:pt-0"
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}>

          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-xl overflow-hidden shrink-0">
              <img src="/logo192.png" alt="EduPulse" className="w-full h-full object-cover" />
            </div>
            <div className="font-sora font-extrabold text-[20px] text-navy-800 leading-none">
              {showSchool ? school.name : <>EduPulse<span style={{ color: "#C9A227" }}>AI</span></>}
            </div>
          </div>

          {/* Mobile exam tags */}
          {!showSchool && step === "email" && (
            <div className="lg:hidden flex flex-wrap gap-1.5 mb-5">
              {EXAM_TAGS.map(tag => (
                <span key={tag} className="px-2.5 py-0.5 rounded-full text-[10px] font-bold font-sora"
                  style={{ background: "rgba(201,162,39,0.1)", color: "#B45309", border: "1px solid rgba(201,162,39,0.2)" }}>
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-2">
              <Shield size={16} style={{ color: "#C9A227" }} />
              <span className="text-[11px] font-bold font-sora uppercase tracking-widest" style={{ color: "#C9A227" }}>
                {showSchool ? "School Portal" : "Defence Exam Prep"}
              </span>
            </div>
            <h2 className="font-sora text-2xl sm:text-[28px] font-extrabold text-navy-800 leading-tight">
              {step === "otp"     ? "Enter the OTP"
               : step === "profile" ? "Complete your profile"
               : showSchool        ? "Sign in to your portal"
               : "Welcome back"}
            </h2>
            <p className="text-sm text-navy-400 mt-1.5">
              {step === "otp"
                ? <>Code sent to <strong className="text-navy-700">{email}</strong>. Check inbox &amp; spam.</>
                : step === "profile"
                  ? "Just a few details to get you started."
                  : showSchool
                    ? "Enter your school email to continue"
                    : "Enter your email to sign in or create an account."}
            </p>
          </div>

          <AnimatePresence mode="wait">

            {/* ── STEP: email ── */}
            {step === "email" && (
              <motion.div key="email" initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }}>
                <div className="flex flex-col gap-4">
                  <div>
                    <label className="block text-xs font-bold text-navy-500 mb-1.5 font-sora uppercase tracking-wider">
                      Email Address <span style={{ color: "#C9A227" }}>*</span>
                    </label>
                    <div className="relative">
                      <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                        onKeyDown={e => e.key === "Enter" && handleContinue()}
                        placeholder="you@example.com" autoFocus
                        className="w-full px-4 pl-10 py-3.5 rounded-xl border-2 border-navy-150 bg-white text-navy-800 focus:border-navy-400 focus:outline-none transition-colors text-[15px]"
                        autoComplete="email" autoCapitalize="off" autoCorrect="off" spellCheck="false" />
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-navy-300" size={17} />
                    </div>
                  </div>

                  <AnimatePresence>
                    {formErr && (
                      <motion.div className="text-red-600 text-sm font-semibold bg-red-50 p-3 rounded-xl border border-red-200"
                        initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}>
                        {formErr}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <motion.button onClick={handleContinue} disabled={loading}
                    className="w-full py-4 px-6 rounded-xl border-none text-white text-base font-bold font-sora cursor-pointer flex items-center justify-center gap-2 transition-all duration-200 disabled:opacity-60"
                    style={{ background: "linear-gradient(135deg, #1A2810, #2D4A1A)", boxShadow: "0 4px 20px rgba(26,40,16,0.3)" }}
                    whileHover={!loading ? { scale: 1.01, y: -1 } : {}}
                    whileTap={!loading ? { scale: 0.98 } : {}}>
                    {loading
                      ? <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Checking…</>
                      : <>Continue <ChevronRight size={16} /></>}
                  </motion.button>

                  {!showSchool && (
                    <div>
                      <div className="flex items-center gap-3 my-1">
                        <div className="flex-1 h-px bg-navy-100" />
                        <span className="text-[12px] font-semibold text-navy-300">or</span>
                        <div className="flex-1 h-px bg-navy-100" />
                      </div>
                      <div className="flex justify-center">
                        <GoogleLogin onSuccess={handleGoogleSuccess} onError={() => setFormErr("Google sign-in failed.")}
                          text="continue_with" shape="rectangular" theme="outline" size="large" />
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* ── STEP: profile (new users only) ── */}
            {step === "profile" && (
              <motion.div key="profile" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 16 }}>
                <div className="flex flex-col gap-4">
                  <button onClick={() => { setStep("email"); setFormErr(""); }}
                    className="flex items-center gap-2 self-start p-2 rounded-lg hover:bg-navy-50 transition-colors bg-transparent border-none cursor-pointer">
                    <ArrowLeft size={18} className="text-navy-500" />
                    <span className="text-[13px] font-semibold text-navy-500">Back</span>
                  </button>

                  {/* Email display (read-only) */}
                  <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-navy-50 border border-navy-100">
                    <Mail size={14} className="text-navy-400 shrink-0" />
                    <span className="text-[14px] text-navy-600 font-medium truncate">{email}</span>
                  </div>

                  {/* Name row */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-bold text-navy-500 mb-1.5 font-sora uppercase tracking-wider">
                        First Name <span style={{ color: "#C9A227" }}>*</span>
                      </label>
                      <div className="relative">
                        <input value={firstName} onChange={e => setFirstName(e.target.value)}
                          placeholder="Arjun" autoFocus
                          className="w-full px-4 pl-10 py-3 rounded-xl border-2 border-navy-150 bg-white text-navy-800 focus:border-navy-400 focus:outline-none transition-colors text-[14px]" />
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 text-navy-300" size={15} />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-navy-500 mb-1.5 font-sora uppercase tracking-wider">Last Name</label>
                      <input value={lastName} onChange={e => setLastName(e.target.value)}
                        placeholder="Singh"
                        className="w-full px-4 py-3 rounded-xl border-2 border-navy-150 bg-white text-navy-800 focus:border-navy-400 focus:outline-none transition-colors text-[14px]" />
                    </div>
                  </div>

                  {/* Mobile */}
                  <div>
                    <label className="block text-xs font-bold text-navy-500 mb-1.5 font-sora uppercase tracking-wider">Mobile Number</label>
                    <div className="relative">
                      <input value={phone} onChange={e => setPhone(e.target.value)}
                        placeholder="+91 98765 43210" inputMode="tel"
                        className="w-full px-4 pl-10 py-3 rounded-xl border-2 border-navy-150 bg-white text-navy-800 focus:border-navy-400 focus:outline-none transition-colors text-[14px]" />
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-navy-300" size={15} />
                    </div>
                  </div>

                  {/* Exam selector */}
                  <div>
                    <label className="block text-xs font-bold text-navy-500 mb-2 font-sora uppercase tracking-wider">
                      Exam Preparing For <span style={{ color: "#C9A227" }}>*</span>
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {EXAM_TAGS.map(ex => {
                        const m = EXAM_META[ex];
                        const active = targetExam === ex;
                        return (
                          <button key={ex} type="button" onClick={() => setTargetExam(ex)}
                            className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl font-bold text-[13px] font-sora cursor-pointer border-2 transition-all duration-150"
                            style={{
                              background:  active ? `${m.color}15` : "#F1F5F9",
                              color:       active ? m.color : "#64748B",
                              borderColor: active ? `${m.color}60` : "transparent",
                            }}>
                            {m.emoji} {ex}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <AnimatePresence>
                    {formErr && (
                      <motion.div className="text-red-600 text-sm font-semibold bg-red-50 p-3 rounded-xl border border-red-200"
                        initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}>
                        {formErr}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <motion.button onClick={handleSendOTP} disabled={loading}
                    className="w-full py-4 px-6 rounded-xl border-none text-white text-base font-bold font-sora cursor-pointer flex items-center justify-center gap-2 transition-all duration-200 disabled:opacity-60"
                    style={{ background: "linear-gradient(135deg, #1A2810, #2D4A1A)", boxShadow: "0 4px 20px rgba(26,40,16,0.3)" }}
                    whileHover={!loading ? { scale: 1.01, y: -1 } : {}}
                    whileTap={!loading ? { scale: 0.98 } : {}}>
                    {loading
                      ? <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Sending…</>
                      : <><KeyRound size={17} /> Send OTP <ChevronRight size={16} /></>}
                  </motion.button>
                </div>
              </motion.div>
            )}

            {/* ── STEP: otp ── */}
            {step === "otp" && (
              <motion.div key="otp" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }}>
                <div className="flex flex-col gap-4">
                  <button onClick={() => { setStep("email"); setOtp(["","","","","",""]); setOtpErr(""); setFormErr(""); }}
                    className="flex items-center gap-2 self-start p-2 rounded-lg hover:bg-navy-50 transition-colors bg-transparent border-none cursor-pointer">
                    <ArrowLeft size={18} className="text-navy-500" />
                    <span className="text-[13px] font-semibold text-navy-500">Back</span>
                  </button>

                  <label className="block text-xs font-bold text-navy-500 mb-1 font-sora uppercase tracking-wider">Enter OTP</label>
                  <div className="flex gap-2 sm:gap-3 justify-between">
                    {otp.map((digit, idx) => (
                      <input key={idx} ref={el => otpRefs.current[idx] = el}
                        type="text" inputMode="numeric" pattern="[0-9]*" maxLength={1}
                        value={digit}
                        onChange={e => handleOtpChange(idx, e.target.value)}
                        onKeyDown={e => handleOtpKeyDown(idx, e)}
                        autoComplete={idx === 0 ? "one-time-code" : "off"}
                        autoFocus={idx === 0}
                        className="w-11 sm:w-13 h-12 sm:h-14 text-center font-bold rounded-xl border-2 border-navy-200 focus:border-navy-500 focus:outline-none transition-colors text-navy-800 bg-white"
                        style={{ fontSize: "max(20px, 1.25rem)" }} />
                    ))}
                  </div>

                  <AnimatePresence>
                    {otpErr && (
                      <motion.div className="text-red-600 text-sm font-semibold bg-red-50 p-3 rounded-xl border border-red-200"
                        initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}>
                        {otpErr}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <motion.button onClick={handleVerifyOTP} disabled={loading}
                    className="w-full py-4 px-6 rounded-xl border-none text-white text-base font-bold font-sora cursor-pointer flex items-center justify-center gap-2 transition-all duration-200 disabled:opacity-60"
                    style={{ background: "linear-gradient(135deg, #C9A227, #D97706)", boxShadow: "0 4px 20px rgba(201,162,39,0.3)" }}
                    whileHover={!loading ? { scale: 1.01, y: -1 } : {}}
                    whileTap={!loading ? { scale: 0.98 } : {}}>
                    {loading
                      ? <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Signing in…</>
                      : <>Verify & Continue <ChevronRight size={16} /></>}
                  </motion.button>

                  <div className="text-center">
                    <button onClick={handleResendOTP} disabled={countdown > 0 || loading}
                      className="bg-transparent border-none text-[13px] font-semibold cursor-pointer font-body p-0 disabled:text-navy-300 disabled:cursor-not-allowed"
                      style={{ color: countdown > 0 ? undefined : "#C9A227" }}>
                      {countdown > 0 ? `Resend OTP in ${countdown}s` : "Resend OTP"}
                    </button>
                  </div>
                </div>
              </motion.div>
            )}

          </AnimatePresence>

          <div className="flex items-center justify-center gap-2 mt-8 text-navy-300 text-[11px]">
            <Lock size={11} /><span>Secured with 256-bit encryption</span>
          </div>
          <p className="text-center text-[11px] text-navy-200 mt-3">
            Powered by <span className="font-sora font-bold text-navy-400">eduPulse<span style={{ color: "#C9A227" }}>AI</span></span>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
